import React, { useState } from "react";
import { SEC_DataApiPost } from "../../func.js";
import "./AssignUserWithNote.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiSave3Fill } from "react-icons/ri";
import { CgSpinner } from "react-icons/cg";
import { FiPaperclip } from "react-icons/fi";
import useS3FileUpload from "../../hooks/useS3FileUpload";
import { userAppData } from "../../MainApp";
import { FileDropzone } from "../formComponents.js";
import { SelectUserComponent } from "./SelectUserComponent.js";

export function AssignUserWithNote({
  requestId,
  url,
  usersArray,
  updateApiCallFunction,
  placeholder = "Enter your note here...",
  setActiveOption,
  cardView = false,
  showPrivateToggle = false,
  uploadModuleType = "itRequests",
  permissionsKey = "itRequestsData"
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [note, setNote] = useState("");
  const [privateNote, setPrivateNote] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [postApiErrorState, setPostApiErrorState] = useState(false);
  const [files, setFiles] = useState([]);
  const [showDropzone, setShowDropzone] = useState(false);
  const { uploadFiles } = useS3FileUpload(uploadModuleType);
  const canManageRequests = !!userAppData?.[permissionsKey]?.allowManageRequests;
  const showPrivateNoteToggle = showPrivateToggle && canManageRequests;

  const handleUserChange = (user) => {
    setSelectedUser(user);
  };

  async function saveAssignment() {
    if (!selectedUser) {
      toast.warn("⚠️ Please select a user before submitting.", { autoClose: 3000, position: "top-right" });
      return;
    }

    if (isLoading) return;
    setIsLoading(true);
    setPostApiErrorState(false);

    try {
      let uploadedFiles = [];
      const validFiles = files.filter(f => f && f.file && typeof f.fileName === 'string');
      if (validFiles.length > 0) {
        const filesToUpload = validFiles.map(f => ({
          fileName: f.fileName,
          fileSize: f.fileSize,
          file: f.file,
        }));
        uploadedFiles = await uploadFiles(filesToUpload, userAppData.cogId, { requestId });
      }

      const formData = {
        requestId,
        cogId: selectedUser.cogId,
        note,
        privateNote,
        files: uploadedFiles,
      };

      const response = await SEC_DataApiPost(url, JSON.stringify(formData), {}, () => setPostApiErrorState(true));

      if (response && response.ResponseMetadata?.HTTPStatusCode === 200) {
        toast.success("✅ Request assigned!", { autoClose: 3000, position: "top-right" });
        updateApiCallFunction(requestId);
        setNote("");
        setPrivateNote(false);
        setFiles([]);
        setSelectedUser(null);
        setShowDropzone(false);
        if (setActiveOption) setActiveOption(null);
      } else {
        toast.error("❌ Failed to assign user. Please try again.", { autoClose: 3000, position: "top-right" });
      }
    } catch (error) {
      console.error("Error assigning user", error);
      toast.error("❌ Failed to assign user. Please try again.", { autoClose: 3000, position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={`note-input-container${cardView ? ' note-input-container-card' : ''}`}>
      <SelectUserComponent
        usersArray={usersArray}
        selectedUser={selectedUser}
        onChange={handleUserChange}
        isDisabled={isLoading}
      />

      <div className={`note-inputandSave-container${cardView ? ' note-inputandSave-container-card' : ''}`} style={{ marginTop: '10px' }}>
        <div className="note-text-block">
          <textarea
            className="note-input-text-area"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            disabled={isLoading}
          />

          {showPrivateNoteToggle && (
            <div className="note-private-toggle-row">
              <span className="note-private-toggle-label">Private note</span>
              <label className="note-private-switch" aria-label="Private note toggle">
                <input
                  type="checkbox"
                  checked={privateNote}
                  onChange={(e) => setPrivateNote(e.target.checked)}
                  disabled={isLoading}
                />
                <span className="note-private-slider" />
              </label>
            </div>
          )}
        </div>

        <div className={`note-input-actions${cardView ? ' note-input-actions-card' : ''}`}>
          <button onClick={() => setShowDropzone(!showDropzone)} disabled={isLoading} className="note-toggle-attachments-button">
              <FiPaperclip className="note-save-icon" color="white" />
          </button>

          <button onClick={saveAssignment} disabled={isLoading} className="note-save-button">
            {isLoading ? (
              <span className="note-save-button-spinner">
                <CgSpinner color="white" />
              </span>
            ) : (
              <RiSave3Fill className="note-save-icon" color="white" />
            )}
          </button>
        </div>
      </div>

      {showDropzone && (
        <div className="note-dropzone-wrapper">
          <FileDropzone
            filesState={files}
            fileStateSet={setFiles}
            disabled={isLoading}
            maxFiles={10}
            maxTotalSizeMB={50}
            fullWidth
          />
        </div>
      )}
    </div>
  );
}
